import {
  Button,
  Form,
  FormItem,
  Input,
  InputNumber,
  Message,
  useForm,
} from "@/frontend/components/common";
import { poolPredictionService } from "@/frontend/services";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type {
  ApiResponse,
  CreatePoolPredictionRequestDTO,
  PoolPredictionResponseDTO,
  UpdatePoolPredictionRequestDTO,
} from "@/shared/dto";

interface SportsPredictionFormValues {
  poolId: string;
  participantId: string;
  fixtureId: string;
  homeScore: number;
  awayScore: number;
  homePenalties?: number | null;
  awayPenalties?: number | null;
}

interface SportsPredictionFormProps {
  predictionId?: string;
  initialValues?: Partial<SportsPredictionFormValues>;
}

export function SportsPredictionForm({ predictionId, initialValues }: SportsPredictionFormProps) {
  const navigate = useNavigate();
  const [form] = useForm();
  const isEditMode = !!predictionId;

  useEffect(() => {
    if (initialValues && isEditMode) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, isEditMode, form]);

  const handleSubmit = async (values: SportsPredictionFormValues) => {
    try {
      let response: ApiResponse<PoolPredictionResponseDTO>;

      if (isEditMode && predictionId) {
        const payload: UpdatePoolPredictionRequestDTO = {
          homeScore: values.homeScore,
          awayScore: values.awayScore,
          homePenalties: values.homePenalties ?? null,
          awayPenalties: values.awayPenalties ?? null,
        };

        response = await poolPredictionService.update(predictionId, payload);
      } else {
        const payload: CreatePoolPredictionRequestDTO = {
          poolId: values.poolId,
          participantId: values.participantId,
          fixtureId: values.fixtureId,
          homeScore: values.homeScore,
          awayScore: values.awayScore,
          homePenalties: values.homePenalties ?? null,
          awayPenalties: values.awayPenalties ?? null,
        };

        response = await poolPredictionService.create(payload);
      }

      if (response.success && response.data) {
        Message.success({
          content: isEditMode ? "Predicción actualizada correctamente" : "Predicción creada correctamente",
        });
        navigate("/sports-predictions");
      } else {
        Message.error({
          content: response.error || (isEditMode ? "Error al actualizar predicción" : "Error al crear predicción"),
        });
      }
    } catch (error) {
      Message.error({
        content: error instanceof Error ? error.message : "Error al procesar la predicción",
      });
    }
  };

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      initialValues={{
        homeScore: 0,
        awayScore: 0,
        homePenalties: null,
        awayPenalties: null,
      }}
      className="w-full"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isEditMode && (
          <>
            <FormItem
              name="poolId"
              label="Pool ID"
              rules={[{ required: true, message: "El poolId es requerido" }]}
            >
              <Input placeholder="ID de la polla/grupo" size="large" />
            </FormItem>

            <FormItem
              name="participantId"
              label="Participante ID"
              rules={[{ required: true, message: "El participantId es requerido" }]}
            >
              <Input placeholder="ID del usuario participante" size="large" />
            </FormItem>

            <FormItem
              name="fixtureId"
              label="Fixture ID"
              rules={[{ required: true, message: "El fixtureId es requerido" }]}
            >
              <Input placeholder="ID del partido" size="large" />
            </FormItem>
          </>
        )}

        <FormItem
          name="homeScore"
          label="Goles Local"
          rules={[{ required: true, message: "Los goles locales son requeridos" }]}
        >
          <InputNumber min={0} size="large" className="w-full" />
        </FormItem>

        <FormItem
          name="awayScore"
          label="Goles Visitante"
          rules={[{ required: true, message: "Los goles visitantes son requeridos" }]}
        >
          <InputNumber min={0} size="large" className="w-full" />
        </FormItem>

        <FormItem name="homePenalties" label="Penales Local (opcional)">
          <InputNumber min={0} size="large" className="w-full" />
        </FormItem>

        <FormItem name="awayPenalties" label="Penales Visitante (opcional)">
          <InputNumber min={0} size="large" className="w-full" />
        </FormItem>
      </div>

      <div className="flex justify-end gap-4 mt-6">
        <Button type="primary" htmlType="submit" size="large">
          {isEditMode ? "Actualizar Predicción" : "Crear Predicción"}
        </Button>
        <Button size="large" onClick={() => navigate("/sports-predictions")}>
          Cancelar
        </Button>
      </div>
    </Form>
  );
}
